import { FormikErrors, FormikTouched } from "formik";
import React, { ChangeEvent, FocusEvent, useEffect, useRef } from "react";
import { Box, Checkbox, Input, Label, Select, Text, Textarea, ThemeUIStyleObject } from "theme-ui";
import ReactDatePicker from "react-datepicker";
import { useSelector } from "react-redux";
import 'react-datepicker/dist/react-datepicker.css';
import * as moment from "moment";
import calendarIcon from '../assets/images/calendar-2.svg';
interface InputformProps<T extends HTMLElement = HTMLInputElement> {
    label?: string;
    Placeholder: string;
    Name: string;
    sx?: ThemeUIStyleObject;
    Title_sx?: ThemeUIStyleObject;
    type?: 'button' | 'checkbox' | 'color' | 'date' | 'datetime-local' | 'email' | 'file' | 'hidden' | 'image' | 'month' | 'number' | 'password' | 'radio' | 'range' | 'reset' | 'search' | 'submit' | 'tel' | 'text' | 'time' | 'url' | 'week' | 'select' | 'textarea' | undefined;
    values: string | number | boolean | Date | null | undefined;
    inputClassName?: string;
    wrapperClassName?: string;
    touched?: boolean | FormikTouched<unknown> | FormikTouched<unknown>[] | undefined;
    errors?: string | string[] | FormikErrors<unknown> | FormikErrors<unknown>[] | undefined;
    Max_Length?: number;
    OnChange?: (e: ChangeEvent<T>) => void;
    onClick?: React.MouseEventHandler<HTMLElement>;
    onBlur?: (e: FocusEvent<T>) => void;
    onKeyDown?: (e: React.KeyboardEvent<T>) => void;
    handleSubmit?: () => void;
    autoFocus?: boolean;
    disabled?: boolean;
    description?: string;
    options?: string[] | { value: string; label: string }[];
    mb?: number | undefined | Array<number>;
    asterisk?: boolean;
    max?: string | number;
    dateFormat?: string;
    onFocus?: (e: FocusEvent<T>) => void;
    endIcon?: React.ReactNode;
    readOnly?: boolean;
}

const DateCustomInput = React.forwardRef<HTMLInputElement, React.ComponentPropsWithoutRef<typeof Input>>((props, ref) => {
    return (
        <Box sx={{ position: 'relative', display: 'block', width: '100%' }}>
            <Input
                {...props}
                ref={ref}
                sx={{
                    ...props.sx,
                    pr: 40,
                }}
            />
            <img
                src={calendarIcon}
                alt="calendar"
                style={{
                    position: 'absolute',
                    right: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    width: '18px',
                    height: '18px'
                }}
            />
        </Box>
    );
});

export function Inputform<T extends HTMLElement = HTMLInputElement>(props: InputformProps<T>) {
    const {
        label,
        type,
        Placeholder,
        OnChange = () => null,
        Title_sx,
        sx,
        Name,
        inputClassName,
        wrapperClassName,
        onClick = () => null,
        values,
        onBlur = () => null,
        onKeyDown = () => null,
        handleSubmit = () => null,
        autoFocus = false,
        description = "",
        touched = false,
        disabled = false,
        readOnly = false,
        errors = "",
        options = [],
        mb,
        asterisk,
        max,
        dateFormat,
        onFocus = () => null,
        endIcon,
    } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { auther } = useSelector((state: any) => state.auth);
    const isAllowedToSelectRedDates = auther?.priorityDueDateAccess;

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    useEffect(() => {
        if (type === 'textarea' && textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [values, type]);


    return (
        <Box
            mb={mb}
            className={wrapperClassName}
        >
            <Text variant="SelectLabel" sx={sx}>
                {label}
                {asterisk && <span className="mx-1 text-danger">*</span>}
            </Text>
            {type === 'checkbox' ? (
                <Label>
                    <Checkbox
                        name={Name}
                        checked={values as boolean}
                        onChange={OnChange as unknown as React.ChangeEventHandler<HTMLInputElement>}
                        onBlur={onBlur as unknown as React.FocusEventHandler<HTMLInputElement>}
                        disabled={disabled}
                        sx={{ ...sx, borderColor: (errors && touched) || errors === 'Date is fully booked.' ? "red" : undefined }}
                    />
                </Label>
            ) : type === 'select' ? (
                <Select
                    name={Name}
                    value={values as string | number | undefined}
                    onChange={OnChange as unknown as React.ChangeEventHandler<HTMLSelectElement>}
                    onBlur={onBlur as unknown as React.FocusEventHandler<HTMLSelectElement>}
                    disabled={disabled}
                    sx={{ ...sx, borderColor: (errors && touched) || errors === 'Date is fully booked.' ? "red" : undefined }}
                >
                    {options.map((option, index) => (
                        typeof option === 'string' ? (
                            <option key={index} value={option}>
                                {option}
                            </option>
                        ) : (
                            <option key={index} value={option.value}>
                                {option.label}
                            </option>
                        )
                    ))}
                </Select>
            ) : type === 'date' ? (
                <>
                    <ReactDatePicker
                        selected={values && (moment as any)(values).isValid() ? (moment as any)(values).toDate() : null}
                        onChange={(date: Date | null) => {
                            const dateStr = date ? (moment as any)(date).format("YYYY-MM-DD") : "";
                            const event = {
                                target: {
                                    name: Name,
                                    value: dateStr
                                }
                            } as unknown as ChangeEvent<T>;
                            OnChange(event);
                        }}
                        placeholderText={Placeholder}
                        dateFormat={dateFormat || "yyyy-MM-dd"}
                        strictParsing={true}
                        autoFocus={autoFocus}
                        disabled={disabled}
                        popperPlacement="bottom-start"
                        portalId="root-portal"
                        dayClassName={(date: Date) => {
                            const dateStr = (moment as any)(date).format("YYYY-MM-DD");
                            const count = (sx as any)?.dayCounts?.[dateStr] || 0;
                            const day = (moment as any)(date).day();
                            const isWeekend = day === 0 || day === 6;

                            if (isWeekend) return "";
                            if (count >= 35) return "calendar-day-high";
                            if (count >= 16 && count <= 34) return "calendar-day-medium";
                            if (count >= 5 && count <= 15) return "calendar-day-low";
                            return "";
                        }}
                        // filterDate={(date: Date) => {
                        //     if (isAllowedToSelectRedDates) return true;
                        //     const dateStr = (moment as any)(date).format("YYYY-MM-DD");
                        //     const count = (sx as any)?.dayCounts?.[dateStr] || 0;
                        //     return count < 31; // Block if count >= 31
                        // }}//future todo
                        customInput={
                            <DateCustomInput
                                variant="PersonalDetailsInput"
                                name={Name}
                                type="text"
                                max={max ? max : ''}
                                autoComplete="off"
                                disabled={disabled}
                                className={`form-textcontrol ${inputClassName}`}
                                sx={{
                                    py: [9, 10, 13, 13, 14, 15],
                                    px: 18,
                                    borderRadius: "10px",
                                    ...(sx as any),
                                    borderColor: (errors && touched) || errors === 'Date is fully booked.' ? "red" : undefined,
                                }}
                                onClick={onClick}
                                onBlur={onBlur as unknown as React.FocusEventHandler<HTMLInputElement>}
                                onKeyDown={onKeyDown as unknown as React.KeyboardEventHandler<HTMLInputElement>}
                            />
                        }
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 15px', borderTop: '1px solid #eaeaea', backgroundColor: 'white' }}>
                            <span
                                style={{ color: '#007bff', cursor: 'pointer', fontWeight: 500, fontSize: '14px' }}
                                onClick={() => {
                                    const event = { target: { name: Name, value: "" } } as unknown as ChangeEvent<T>;
                                    OnChange(event);
                                }}
                            >
                                Clear
                            </span>
                            <span
                                style={{ color: '#007bff', cursor: 'pointer', fontWeight: 500, fontSize: '14px' }}
                                onClick={() => {
                                    const event = { target: { name: Name, value: (moment as any)().format("YYYY-MM-DD") } } as unknown as ChangeEvent<T>;
                                    OnChange(event);
                                }}
                            >
                                Today
                            </span>
                        </div>
                    </ReactDatePicker>
                </>
            ) : type === 'textarea' ? (
                <Textarea
                    variant="PersonalDetailsInput"
                    ref={textareaRef}
                    placeholder={Placeholder}
                    name={Name}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    autoComplete="off"
                    className={`form-textcontrol ${inputClassName}`}
                    sx={{
                        py: [9, 10, 13, 13, 14, 15],
                        px: 18,
                        borderRadius: "10px",
                        ...sx,
                        borderColor: (errors && touched) || errors === 'Date is fully booked.' ? "red" : undefined,
                        overflowY: 'auto',
                        resize: 'none',
                        transition: 'height 0.2s ease-in-out',
                        maxHeight: '160px',
                    }}
                    onChange={OnChange as unknown as React.ChangeEventHandler<HTMLTextAreaElement>}
                    onClick={onClick}
                    onFocus={onFocus as unknown as React.FocusEventHandler<HTMLTextAreaElement>}
                    onBlur={onBlur as unknown as React.FocusEventHandler<HTMLTextAreaElement>}
                    onKeyDown={onKeyDown as unknown as React.KeyboardEventHandler<HTMLTextAreaElement>}
                    value={values as string | number | undefined}
                />
            ) : endIcon ? (
                <Box sx={{ position: 'relative', width: '100%' }}>
                    <Input
                        variant="PersonalDetailsInput"
                        ref={inputRef}
                        placeholder={Placeholder}
                        name={Name}
                        type={type}
                        max={max ? max : ''}
                        disabled={disabled}
                        readOnly={readOnly}
                        autoFocus={autoFocus}
                        autoComplete="off"
                        className={`form-textcontrol ${inputClassName}`}
                        sx={{
                            py: [9, 10, 13, 13, 14, 15],
                            px: 18,
                            pr: 38,
                            borderRadius: "10px",
                            ...sx,
                            borderColor: (errors && touched) || errors === 'Date is fully booked.' ? "red" : undefined,
                        }}
                        onChange={OnChange as unknown as React.ChangeEventHandler<HTMLInputElement>}
                        onClick={onClick}
                        onBlur={onBlur as unknown as React.FocusEventHandler<HTMLInputElement>}
                        onKeyDown={onKeyDown as unknown as React.KeyboardEventHandler<HTMLInputElement>}
                        value={values as string | number | undefined}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            right: '14px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            pointerEvents: 'none',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        {endIcon}
                    </Box>
                </Box>
            ) : (
                <Input
                    variant="PersonalDetailsInput"
                    ref={inputRef}
                    placeholder={Placeholder}
                    name={Name}
                    type={type}
                    max={max ? max : ''}
                    disabled={disabled}
                    readOnly={readOnly}
                    autoFocus={autoFocus}
                    autoComplete="off"
                    className={`form-textcontrol ${inputClassName}`}
                    sx={{
                        py: [9, 10, 13, 13, 14, 15],
                        px: 18,
                        borderRadius: "10px",
                        ...sx,
                        borderColor: (errors && touched) || errors === 'Date is fully booked.' ? "red" : undefined,
                    }}
                    onChange={OnChange as unknown as React.ChangeEventHandler<HTMLInputElement>}
                    onClick={onClick}
                    onBlur={onBlur as unknown as React.FocusEventHandler<HTMLInputElement>}
                    onKeyDown={onKeyDown as unknown as React.KeyboardEventHandler<HTMLInputElement>}
                    value={values as string | number | undefined}
                />
            )}

            {errors && (touched || errors === 'Date is fully booked.') ? (
                <Text variant="Inter15Regular30lh" className="text-danger" sx={{ my: "5px", minHeight: "12px", lineHeight: '10px' }}>
                    {errors.toString()}
                </Text>
            ) : (
                <Text variant="Inter15Regular30lh" sx={{ my: "5px", minHeight: "11px" }}>
                    {description}
                </Text>
            )}
        </Box>
    );
}
